import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  MessageBar,
  RoleGuard,
  Table,
  useTable,
} from 'portal-commons';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { faCircleUser, faHourglass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DetailsSection, Divider } from './components';
import AppealHistoryModal from '../AppealHistoryModal';
import { filterAppealCategoriesByVettingType } from '../../utils';
import { selectAppealCategories } from '../../../CSP-Form/selectors';
import { VettingType } from '../../../CSP-Form/constants';
import { VettingAppeal, vettingController } from '@/apis/core';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';

interface IdentityStatusTabProps {
  readOnly?: boolean;
  brandUid: string;
  onAppeal?: () => void;
  onResubmit?: () => void;
}

enum ModalType {
  None = 'none',
  AppealDetails = 'appealDetails',
}

enum ConfirmationType {
  None = 'none',
  ResubmitBrand = 'resubmitBrand',
}

const TableHeaders = [
  {
    id: 'status',
    label: 'Appeal Status',
    sortable: false,
  },
  {
    id: 'createDate',
    label: 'Request Date',
    sortable: false,
  },
  {
    id: 'statusUpdateDate',
    label: 'Status Update Date',
    sortable: false,
  },
  {
    id: 'details',
    label: 'Details',
    sortable: false,
  },
];

const IdentityStatusTab: FunctionComponent<IdentityStatusTabProps> = ({
  readOnly = false,
  brandUid,
  onAppeal,
  onResubmit,
}) => {
  const { loading: tableLoading, tableData: appeals } = useTable<VettingAppeal>(
    async () => {
      const records = brandUid
        ? await vettingController.getAppeals(brandUid, {
            vettingClassType: 'IDENTITY',
          })
        : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    {
      deps: [brandUid],
      inMemory: true,
    }
  );
  const appealCategories = useSelector(selectAppealCategories);
  const [modalType, setModalType] = useState(ModalType.None);
  const [activeAppeal, setActiveAppeal] = useState<VettingAppeal>();
  const [confirmationType, setConfirmationType] = useState(
    ConfirmationType.None
  );
  const tableData = useMemo(() => {
    return appeals.map((appeal) => ({
      status: (
        <span data-testid="status">{humanize(appeal.status) || 'N/A'}</span>
      ),
      createDate: (
        <span data-testid="createDate">
          {convertTimeWithTimezone(
            appeal.createDate,
            undefined,
            'MM/DD/YYYY'
          ) || 'N/A'}
        </span>
      ),
      statusUpdateDate:
        appeal.status === 'PENDING' ? (
          <Flex
            sx={{ alignItems: 'center', gap: 'xs' }}
            data-testid="statusUpdateDate"
          >
            <Box>
              <FontAwesomeIcon icon={faHourglass} />
            </Box>
            Pending
          </Flex>
        ) : (
          <span data-testid="statusUpdateDate">
            {convertTimeWithTimezone(
              appeal.statusUpdateDate,
              undefined,
              'MM/DD/YYYY'
            ) || 'N/A'}
          </span>
        ),
      details: (
        <Button
          sx={{ height: 'fit-content', padding: 0 }}
          size="sm"
          shape="square"
          variant="transparent"
          onClick={() => {
            setActiveAppeal(appeal);
            setModalType(ModalType.AppealDetails);
          }}
          data-testid="viewDetails"
        >
          View Details
        </Button>
      ),
    }));
  }, [appeals]);
  const identityAppealCategories = useMemo(
    () =>
      filterAppealCategoriesByVettingType(
        appealCategories,
        VettingType.Identity
      ),
    [appealCategories]
  );

  const hideConfirmation = () => setConfirmationType(ConfirmationType.None);

  return (
    <>
      <DetailsSection title="Appeal History" icon={faCircleUser}>
        <Table
          disableHover
          loading={tableLoading}
          headRows={TableHeaders}
          data={tableData}
          emptyState="no appeals to view"
          emptyBlockHeight="180px"
        />
      </DetailsSection>
      <Divider />
      {confirmationType === ConfirmationType.None ? (
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '24px',
          }}
        >
          <RoleGuard feature="brandDetail.appealIdentity">
            <Button
              size="lg"
              shape="square"
              variant="outline"
              color="secondary"
              disabled={readOnly}
              onClick={onAppeal}
              data-testid="appealIdentityStatus"
            >
              Appeal Identity Status
            </Button>
          </RoleGuard>
          <RoleGuard feature="brandDetail.resubmitBrand">
            <Button
              size="lg"
              shape="square"
              variant="outline"
              color="secondary"
              disabled={readOnly}
              onClick={() =>
                setConfirmationType(ConfirmationType.ResubmitBrand)
              }
              data-testid="resubmitBrand"
            >
              Resubmit Brand
            </Button>
          </RoleGuard>
        </Flex>
      ) : confirmationType === ConfirmationType.ResubmitBrand ? (
        <MessageBar
          showClose={false}
          type="warning"
          title="Are you sure you want to resubmit brand?"
          primaryAction={{
            label: 'Proceed',
            onClick: () => {
              onResubmit?.();
              hideConfirmation();
            },
          }}
          secondaryAction={{
            label: 'Cancel',
            onClick: hideConfirmation,
          }}
          data-testid="resubmitBrandConfirmation"
        >
          You will be charged $4.50 every time you resubmit a brand for identity
          verification.
        </MessageBar>
      ) : null}
      {activeAppeal && (
        <AppealHistoryModal
          open={modalType === ModalType.AppealDetails}
          title="Identity Status"
          appeal={activeAppeal}
          appealCategories={identityAppealCategories}
          onClose={() => setModalType(ModalType.None)}
        />
      )}
    </>
  );
};

export default IdentityStatusTab;
